import { CheckCircle, AlertCircle, XCircle, Loader2 } from "lucide-react";
import { Badge } from "~/components/ui/badge";

export const StepHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div>
    <h3 className="text-lg font-semibold mb-1">{title}</h3>
    {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
  </div>
);

export type ValidationStatus = "passed" | "warning" | "running" | "failed" | "uploading";

const statusConfig: Record<
  ValidationStatus,
  {
    variant: "success" | "warning" | "default" | "destructive" | "secondary";
    icon: React.ReactNode;
    label: string;
  }
> = {
  passed: {
    variant: "success",
    icon: <CheckCircle className="w-3 h-3" />,
    label: "PASSED",
  },
  warning: {
    variant: "warning",
    icon: <AlertCircle className="w-3 h-3" />,
    label: "WARNING",
  },
  running: {
    variant: "default",
    icon: <Loader2 className="w-3 h-3 animate-spin" />,
    label: "RUNNING",
  },
  failed: {
    variant: "destructive",
    icon: <XCircle className="w-3 h-3" />,
    label: "FAILED",
  },
  uploading: {
    variant: "secondary",
    icon: <Loader2 className="w-3 h-3 animate-spin" />,
    label: "UPLOADING",
  },
};

export const StatusBadge = ({ status }: { status: ValidationStatus }) => {
  const config = statusConfig[status];
  return (
    <Badge variant={config.variant}>
      {config.icon}
      {config.label}
    </Badge>
  );
};

export interface ValidationJobCardProps {
  title: string;
  description: string;
  result: string;
  status: ValidationStatus;
  details?: string;
  warnings?: string[];
}

export const ValidationJobCard = ({
  title,
  description,
  result,
  status,
  details,
  warnings,
}: ValidationJobCardProps) => (
  <div className="border rounded-lg p-4">
    <div className="flex items-center justify-between mb-2">
      <h4 className="font-medium">{title}</h4>
      <StatusBadge status={status} />
    </div>
    <p className="text-sm text-gray-600 mb-1">{description}</p>
    <p className="text-sm text-gray-700">{result}</p>
    {details && <p className="text-sm text-gray-600 mt-2">{details}</p>}
    {warnings && warnings.length > 0 && (
      <ul className="bg-yellow-50 mt-2 p-2 rounded-lg border border-yellow-200 text-sm text-yellow-700 space-y-0.5">
        {warnings.map((warning, idx) => (
          <li key={idx} className="flex items-start gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{warning}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const SummaryItem = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex justify-between">
    <span className="text-gray-600">• {label}:</span>
    <span className="font-medium">{value}</span>
  </div>
);

export const StatCard = ({
  icon,
  label,
  value,
  color = "gray",
}: {
  icon?: React.ReactNode;
  label: string;
  value: string | number;
  color?: "gray" | "blue" | "green" | "red";
}) => {
  const colorClasses = {
    gray: "bg-gray-600",
    blue: "bg-blue-600",
    green: "bg-green-600",
    red: "bg-red-600",
  };

  return (
    <div className="flex flex-col gap-1 justify-between p-4 border rounded-lg">
      <div className="flex gap-2">
        <div className="py-0.5">
          {icon || <div className={`w-4 h-4 ${colorClasses[color]} rounded aspect-square`}></div>}
        </div>
        <span className="text-sm font-medium text-gray-600">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
};
